<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'worksheets',
    path: '/worksheets',
    component: 'WorksheetList',
    title: () => gettext('Worksheets'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WorksheetList',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'number',
      name: $fn.value.pgettext('Worksheets', 'Number'),
      type: 'command',
      width: '200px',
      formatter: {
        name: 'routeLink',
        to: 'worksheet',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'status',
      name: $fn.value.pgettext('Worksheets', 'Status'),
      width: '150px',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'gettext',
            context: 'WorksheetStatus',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'statusDot',
          },
        ],
      },
    },
    {
      field: 'worksheet_date',
      name: $fn.value.pgettext('Worksheets', 'Date'),
      type: 'date',
    },
    {
      field: 'description',
      width: '200px',
      name: $fn.value.pgettext('Worksheets', 'Description'),
    },
    {
      field: 'rental_name',
      name: $fn.value.pgettext('Worksheets', 'Rental'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'rental',
        navigateOnIconClick: true,
        params: {
          id: 'rental_id',
        },
      },
    },
    {
      field: 'company_name',
      name: $fn.value.pgettext('Worksheets', 'Company'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'company_id',
        },
      },
    },
    {
      field: 'client_name',
      width: '200px',
      name: $fn.value.pgettext('Worksheets', 'Client'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'contractor_name',
      width: '200px',
      name: $fn.value.pgettext('Worksheets', 'Contractor'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'contractor_id',
        },
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('integro_api/list_worksheet');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Worksheets'),
      icon: $config.value.worksheet.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_dvqt8j3fk_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_8nlrzrkg8_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_showModal_dabo3a2d5_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Worksheet',
      props: {
        size: 'lg',
      },
    };
  };
  const act_2095089031469157_reloadSlickgrid_1nkangdaq_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'worksheets',
    };
  };
  const act_2095089031469157_request_qrkg436r7_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_worksheets',
      data: { worksheets: selectedItems.value.map((p) => p.id) },
    };
  };

  const act_7315092382398562_closeModal_dvqt8j3fk =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_dvqt8j3fk_getActionArgs,
    });
  const inst_evh_7315092382398562_close_eorz0qggp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_dvqt8j3fk,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_eorz0qggp],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_z3j5pnl2j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_94zqr81ae =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_z3j5pnl2j,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_94zqr81ae],
    event: 'reload',
  });
  const act_2248226175642056_setData_8nlrzrkg8 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_8nlrzrkg8_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_aj681dafb =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_8nlrzrkg8,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_aj681dafb],
      event: 'selectedRowsChanged',
    });
  const act_4615685607825208_showModal_dabo3a2d5 =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_dabo3a2d5_getActionArgs,
    });
  const inst_evh_4615685607825208_click_xfuif2yu5 =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_dabo3a2d5,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_xfuif2yu5],
    event: 'click',
  });
  const act_2095089031469157_reloadSlickgrid_1nkangdaq =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2095089031469157_reloadSlickgrid_1nkangdaq_getActionArgs,
    });
  const inst_evh_2095089031469157_success_ciat9czi3 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadSlickgrid_1nkangdaq,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_2095089031469157_request_qrkg436r7 = new actionTypes.RequestAction({
    actionArgs: act_2095089031469157_request_qrkg436r7_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_2095089031469157_success_ciat9czi3],
  });
  const inst_evh_2095089031469157_click_tip6gddxu =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_request_qrkg436r7,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_tip6gddxu],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_eorz0qggp,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_94zqr81ae,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_aj681dafb,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_4615685607825208_click_xfuif2yu5,
    inst_evh_4615685607825208_click,
    inst_evh_2095089031469157_success_ciat9czi3,
    inst_evh_2095089031469157_click_tip6gddxu,
    deleteItems: inst_evh_2095089031469157_click_tip6gddxu,
    inst_evh_2095089031469157_click,
  };

  const evh_7315092382398562_close_eorz0qggp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_eorz0qggp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_94zqr81ae = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_94zqr81ae.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_aj681dafb = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_aj681dafb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_xfuif2yu5 = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_xfuif2yu5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_ciat9czi3 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_ciat9czi3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_tip6gddxu = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_tip6gddxu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WorksheetList"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="worksheets"
      :headerTitle="gettext('Worksheets')"
      :items="ds"
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            :icon="$config.worksheet.createWorksheetIcon"
            :text="gettext('New worksheet...')"
            size="sm"
            variant="light"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <ItpButton
            icon="fa-trash"
            :text="gettext('Delete')"
            size="sm"
            variant="light"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/worksheets/worksheets.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
